import React, { useCallback, useState } from 'react';
import { useLocation } from '@reach/router';
import clsx from 'clsx';
import { makeStyles, Theme, Hidden } from '@material-ui/core';

import { useModal, useUser } from 'hooks';

import { path } from 'commons/router/routerConfig';
import { Auth, AUTHORITIES } from 'commons/constants/KeyFlowConstants';

import { HeaderLogo, BoxIcon, UserIcon, DrawerIcon } from 'asset/images';
import TimelineIcon from '@material-ui/icons/Timeline';

import { LibraryList } from 'components/Library';
import { MobileDrawer } from 'components/Drawer';
import Statistics from 'components/Statistics/Statistics';

const useStyles = makeStyles((theme: Theme) => ({
  headerContainer: {
    padding: '13px 15px 42px 15px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    position: 'relative',
    zIndex: 10,
    [theme.breakpoints.down('md')]: {
      width: 'auto',
      height: '67px',
      minWidth: '290px',
      margin: 'auto',
      alignItems: 'center',
      paddingLeft: '15px',
      paddingRight: '15px',
      paddingTop: 0,
      paddingBottom: 0,
    },
  },

  logoContainer: {
    [theme.breakpoints.down('md')]: {
      height: '42px',
      paddingTop: '5px',
      alignSelf: 'flex-start',
    },
    '& > img': {
      height: '100%',
    },
  },
  hoverCursor: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  menuContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingBottom: '3px',
    color: 'rgba(255, 255, 255, 1.0)',
    fontSize: '14px',
  },
  menuItem: {
    margin: '0 0px 0 35px',
    '& > img': {
      width: '1.5em',
      margin: '0 5px 0 0',
    },
  },
  statistics: {
    display: 'flex',
    height: 'full',
    gap: '6px',
    backgroundColor: 'transparent',
    border: '0px solid white',
    color: 'rgba(255, 255, 255, 1.0)',
    fontWeight: 'bold',
    fontSize: '1.0em',
    cursor: 'pointer',
  },
  buttonItem: {
    backgroundColor: 'transparent',
    border: '0px solid white',
    color: 'rgba(255, 255, 255, 1.0)',
    fontWeight: 'bold',
    fontSize: '1.0em',

    '&:hover': {
      cursor: 'pointer',
    },
  },
  userIcon: {
    width: '1.1em !important',
  },
  drawerIcon: {
    width: '20px',
    height: '20px',
  },
  drawerButton: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

function Header() {
  const classes = useStyles();

  const location = useLocation();

  /***
   * 2021. 10. 27
   * 로그인 로직을 토큰 방식으로 변경함에따라 (기존 : 권한 API에서 가져옴)
   * 로컬 스토리지에 유저 정보 저장, 유저정보 가져오는 방식을 리덕스->로컬스토리지 로 변경
   */
  // const {userId} = useSelector((state: RootState) => state.user);
  const userId = localStorage.getItem(Auth.USER_ID);
  const authorities = localStorage.getItem(Auth.AUTHORITIES) || '';
  const hasAuth = authorities.includes(AUTHORITIES.STATISTICS_VIEW);

  const { onLogout: onLogoutUser, onAuthenticate } = useUser();
  const { onOpenModal } = useModal();

  const [open, setOpen] = useState<boolean>(false);

  const onLogout = useCallback(
    event => {
      onLogoutUser();
    },
    [onLogoutUser],
  );

  const onOpenLibrary = useCallback(() => {
    onOpenModal(<LibraryList />);
  }, [LibraryList, onOpenModal]);

  const onOpenStatistics = useCallback(() => {
    onOpenModal(<Statistics />);
  }, [Statistics, onOpenModal]);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const onClickLogo = useCallback(() => {
    window.open(path.SEARCH_BY_DATE);
  }, []);

  return (
    <>
      <header className={clsx(classes.headerContainer)}>
        <div className={clsx(classes.logoContainer, classes.hoverCursor)} onClick={onClickLogo}>
          <img src={HeaderLogo} />
        </div>
        <div className={classes.menuContainer}>
          <Hidden mdDown>
            {hasAuth && (
              <button className={clsx(classes.statistics)} onClick={onOpenStatistics}>
                <TimelineIcon fontSize="medium" />
                통계
              </button>
            )}
            <span className={clsx(classes.menuItem)}>
              <img src={UserIcon} className={classes.userIcon} />
              {userId} 님
            </span>
            <button className={clsx(classes.menuItem, classes.buttonItem)} onClick={onLogout}>
              로그아웃
            </button>
            <button className={clsx(classes.menuItem, classes.buttonItem)} onClick={onOpenLibrary}>
              <img src={BoxIcon} />
              보관함
            </button>
          </Hidden>
          <Hidden lgUp>
            <span className={clsx(classes.drawerButton)} onClick={onOpen}>
              <img src={DrawerIcon} className={classes.drawerIcon}></img>
            </span>
          </Hidden>
        </div>
      </header>
      <MobileDrawer open={open} onClose={onClose} onOpen={onOpen} />
    </>
  );
}

export default Header;
